import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogBody, DialogFooter, Button, Textarea,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import type { ServiceVatRate } from '@/api/services';
import {
  useCreateServiceVatRate,
  useUpdateServiceVatRate,
  type ServiceVatPayload,
} from '@/api/services-mutations';
import { DateInput } from '@/components/date-input';
import { NumericInput } from '@/components/numeric-input';

/** Extract a human-readable message from an axios/unknown error. */
function errorMessage(err: unknown): string {
  const res = (err as { response?: { data?: { error_description?: string; error?: string; message?: string } } })?.response;
  return res?.data?.error_description ?? res?.data?.error ?? res?.data?.message ?? 'Something went wrong. Please try again.';
}

type FormState = {
  /** The rate as a whole-number percentage (e.g. "10" → tax_rate 0.1). */
  rate_pct: string;
  effective_from: string;
  effective_to: string;
  description: string;
};

const EMPTY: FormState = { rate_pct: '', effective_from: '', effective_to: '', description: '' };

function toForm(v: ServiceVatRate): FormState {
  return {
    rate_pct: String(Math.round(v.tax_rate * 100 * 100) / 100),
    effective_from: v.effective_from ?? '',
    effective_to: v.effective_to ?? '',
    description: v.description ?? '',
  };
}

export interface ServiceVatDialogProps {
  serviceId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set the dialog edits this row; otherwise it creates a new one. */
  vat?: ServiceVatRate | null;
}

/**
 * Add / Edit dialog for a service VAT-rate row. The user enters a percentage
 * (0–100); it is stored as a 0–1 fraction (`tax_rate`). Creates via
 * `useCreateServiceVatRate` or edits via `useUpdateServiceVatRate`.
 */
export function ServiceVatDialog({ serviceId, open, onOpenChange, vat }: ServiceVatDialogProps) {
  const toast = useToast();
  const isEdit = !!vat;
  const [form, setForm] = useState<FormState>(EMPTY);
  const create = useCreateServiceVatRate(serviceId);
  const update = useUpdateServiceVatRate(serviceId);
  const mutation = isEdit ? update : create;

  useEffect(() => {
    if (open) {
      setForm(vat ? toForm(vat) : EMPTY);
      create.reset();
      update.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, vat]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const pctNum = Number(form.rate_pct);
  const rateInvalid = form.rate_pct.trim() === '' || Number.isNaN(pctNum) || pctNum < 0 || pctNum > 100;
  const fromInvalid = form.effective_from.trim() === '';

  const close = (next: boolean) => {
    if (mutation.isPending) return;
    onOpenChange(next);
  };

  const submit = () => {
    if (rateInvalid || fromInvalid) return;
    const payload: ServiceVatPayload = {
      tax_rate: pctNum / 100,
      effective_from: form.effective_from,
      effective_to: form.effective_to.trim() ? form.effective_to : null,
      description: form.description.trim() || undefined,
    };
    const onSuccess = () => {
      toast.success(isEdit ? 'VAT rate updated' : 'VAT rate added');
      close(false);
    };
    if (isEdit && vat) update.mutate({ vatId: vat.id, data: payload }, { onSuccess });
    else create.mutate(payload, { onSuccess });
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit VAT rate' : 'Add VAT rate'}</DialogTitle>
          <DialogDescription>Enter the rate as a percentage (e.g. 10 for 10%).</DialogDescription>
        </DialogHeader>
        <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {mutation.isError ? <Alert tone="danger" title="Could not save VAT rate">{errorMessage(mutation.error)}</Alert> : null}
          <Field label="Rate (%)" required error={rateInvalid ? 'Enter a value between 0 and 100.' : undefined}>
            {(p) => (
              <NumericInput {...p} min={0} max={100} step="0.01" value={form.rate_pct} invalid={rateInvalid} onChange={(e) => set('rate_pct', e.target.value)} />
            )}
          </Field>
          <Field label="Effective from" required error={fromInvalid ? 'Required' : undefined}>
            {(p) => (
              <DateInput {...p} value={form.effective_from} invalid={fromInvalid} onChange={(e) => set('effective_from', e.target.value)} />
            )}
          </Field>
          <Field label="Effective to" description="Leave blank for an open-ended rate.">
            {(p) => (
              <DateInput {...p} value={form.effective_to} onChange={(e) => set('effective_to', e.target.value)} />
            )}
          </Field>
          <Field label="Description">
            {(p) => (
              <Textarea {...p} rows={2} value={form.description} onChange={(e) => set('description', e.target.value)} />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" disabled={mutation.isPending} onClick={() => close(false)}>Cancel</Button>
          <Button variant="primary" loading={mutation.isPending} disabled={rateInvalid || fromInvalid} onClick={submit}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ServiceVatDialog;
